import { Button, ModalDialog, SyncAltIcon } from "@rothoblaas/design-system";
import { memo, useState, type FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import Icon from "../../../components/ui/Icon";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import { UNIT_SYSTEM_IMPERIAL, UNIT_SYSTEM_METRIC, type UnitSystem } from "../../../types";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";

type UnitSystemToggleProps = {
  // Passed in rather than read from --accent-base: the toggle is portaled into the page header,
  // outside the deck designer workspace that defines that variable.
  accentColor: string;
  // Called once the user confirms the warning: switching keeps the shape and dimensions, resets the rest.
  onChange: (nextUnitSystem: UnitSystem) => void;
};

// Shows the current unit system; clicking asks for confirmation, then switches to the other one.
const UnitSystemToggle: FC<UnitSystemToggleProps> = ({ accentColor, onChange }) => {
  const i18n = useI18n();
  const calculationTranslation = i18n.translation.deckDesignerModule.configurations.calculation;
  const { control } = useFormContext<DeckDesignerConfigurationFormValues>();
  const unitSystem = useWatch({ control, name: "unitSystem", exact: true });
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const isReadOnly = useReadOnly();
  const isImperial = unitSystem === UNIT_SYSTEM_IMPERIAL;

  return (
    <>
      <Button
        type="button"
        variant="secondary"
        color={accentColor}
        disabled={isReadOnly}
        onClick={() => setIsConfirmOpen(true)}
      >
        <Icon glyph={SyncAltIcon} />
        {isImperial ? calculationTranslation.unitSystemImperial : calculationTranslation.unitSystemMetric}
      </Button>
      {isConfirmOpen && (
        <ModalDialog
          onClose={() => setIsConfirmOpen(false)}
          title={calculationTranslation.unitSystemChangeTitle}
          accentColor={accentColor}
          isSubmitting={false}
          buttons={[
            <Button key="cancel" variant="secondary" onClick={() => setIsConfirmOpen(false)}>
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key="confirm"
              onClick={() => {
                setIsConfirmOpen(false);
                onChange(isImperial ? UNIT_SYSTEM_METRIC : UNIT_SYSTEM_IMPERIAL);
              }}
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {calculationTranslation.unitSystemChangeWarning}
        </ModalDialog>
      )}
    </>
  );
};

// Memoized, with a stable onChange: DeckDesigner re-renders on its own state, which the toggle doesn't need.
export default memo(UnitSystemToggle);
